/**
 * Description top info component.
 * Displays channel title, AI icon (for IA Corners), info text, and upgradable plan information
 * in the top section of content descriptions.
 */
import AIIcon from '@components/atoms/icons/AI';
import Info from '../../atoms/text/descriptionEventInfo';
import Plan from '@shared/classes/plan';
import RowContainer from '@components/atoms/container/descriptionTopRowContainer';
import Title from '../../atoms/text/descriptionChannelTitle';
import TitleContainer from '../../atoms/container/descriptionChannelTitleContainer';
import TopContainer from '../../atoms/container/descriptionTopContainer';
import UpgradablePlan from '../plan/upgradablePlan';
import { useMemo } from 'react';

/**
 * Props for the DescriptionTopInfo component.
 *
 * @param {string} channelTitle - Title of the channel
 * @param {boolean} displayAI - Whether to display the AI icon
 * @param {string} info - Additional info text to display
 * @param {Plan | null} upgradablePlan - Upgradable plan information
 */
interface Props {
  channelTitle?: string;
  displayAI?: boolean;
  info?: string;
  upgradablePlan?: Plan | null;
}

/**
 * Renders the top information section of content descriptions.
 * Shows AI icon for IA Corners, channel title, info text, and upgradable plan badge.
 *
 * @returns {JSX.Element} Description top info component
 */
export default function DescriptionTopInfo(parentProps: Props) {
  // Hook
  const { display, info, props, title } = useTopInfo(parentProps);

  // Render
  return (
    <RowContainer>
      {display.AI ? <AIIcon scale={1.5} /> : null}
      <TopContainer>
        <TitleContainer>
          {title ? <Title>{title}</Title> : null}
          {display.upgradablePlan ? (
            <UpgradablePlan {...props.upgradablePlan} />
          ) : null}
        </TitleContainer>
        {info ? <Info>{info}</Info> : null}
      </TopContainer>
    </RowContainer>
  );
}

/**
 * Hook to get the top information of the currently focused asset in a rail.
 */
function useTopInfo(parentProps: Props) {
  // Memoize the children props
  return useMemo(() => {
    const { channelTitle, displayAI, info, upgradablePlan } = parentProps;
    return {
      display: {
        AI: displayAI,
        upgradablePlan: !!upgradablePlan,
      },
      info,
      props: {
        upgradablePlan: {
          fontBold: true,
          plan: upgradablePlan,
          separator: !!channelTitle,
        },
      },
      title: channelTitle,
    };
  }, [parentProps]);
}
